import axios from 'axios'
import qs from 'qs'
import request from '@api/helpers/authorizateRequest'
import { readStoredAuth } from '@api/helpers/crmTabSession'

const BASE_URL = '/operator-workspace/v1'
const SESSION_HEADERS = ['Authorization', 'session-id', 'session-identity', 'unique-id', 'sms-token']
const ENDPOINTS = {
    context: '/context',
    inbox: '/inbox',
    inboxWindow: '/inbox-window',
    counters: '/counters',
    chatStatuses: '/chat-statuses',
    subscription: '/subscriptions/current',
    reference: '/companies/:companyId/reference',
    crmSession: '/companies/:companyId/crm-session',
    chats: '/companies/:companyId/chats',
    chat: '/companies/:companyId/chats/:chatId',
    messages: '/companies/:companyId/chats/:chatId/messages',
    action: '/companies/:companyId/chats/:chatId/:action',
    sendStatus: '/companies/:companyId/chats/:chatId/send-operations/:operationId',
    resolveSend: '/companies/:companyId/chats/:chatId/send-operations/:operationId/resolve',
    archive: '/companies/:companyId/archive',
    transcribe: '/companies/:companyId/chats/:chatId/files/:messageId/transcribe',
    archivedTranscribe: '/companies/:companyId/history-messages/:messageId/transcribe',
    history: '/companies/:companyId/customers/:customerId/history/messages',
    historyList: '/companies/:companyId/customers/:customerId/history',
    historyMessages: '/companies/:companyId/histories/:historyId/messages',
    file: '/companies/:companyId/chats/:chatId/files/:messageId',
    archivedFile: '/companies/:companyId/history-messages/:messageId/file',
    fileContent: '/companies/:companyId/chats/:chatId/files/:messageId/content',
    archivedFileContent: '/companies/:companyId/history-messages/:messageId/file/content',
    cards: '/companies/:companyId/cards/lookup',
    driver: '/companies/:companyId/cards/parks/:parkId/drivers/:driverId',
    driverTab: '/companies/:companyId/cards/parks/:parkId/drivers/:driverId/:section',
    candidate: '/companies/:companyId/cards/candidates/:candidateId',
    document: '/companies/:companyId/cards/parks/:parkId/drivers/:driverId/documents/:documentId',
    comment: '/companies/:companyId/cards/parks/:parkId/drivers/:driverId/comments',
}

const serializeParams = params => qs.stringify(params, { arrayFormat: 'brackets' })

export const httpErrorStatus = error => Number(error?.code) || error?.response?.status || 0
export const isRequestCancelled = error => error?.domainCode === 'request_cancelled'

// A missing id would otherwise reach the server as the literal "undefined" or "null" in the path.
function endpoint(name, params = {}) {
    return BASE_URL + ENDPOINTS[name].replace(/:([a-zA-Z]+)/g, (_, key) => {
        if (params[key] === undefined || params[key] === null || params[key] === '')
            throw Object.assign(new Error('Недостаточно данных для запроса'), { code: 0, domainCode: 'missing_parameter' })
        return encodeURIComponent(params[key])
    })
}

function windowParams(params) {
    if (!params.fingerprints || serializeParams(params).length <= 3500) return params
    const { fingerprints, ...filters } = params
    const known = {}
    let length = serializeParams(filters).length
    // Omitted hashes request full company rows; selected filters must never be truncated.
    Object.entries(fingerprints).forEach(([id, hash]) => {
        const size = serializeParams({ fingerprints: { [id]: hash } }).length + (length ? 1 : 0)
        if (length + size > 3500) return
        known[id] = hash
        length += size
    })
    return { ...filters, ...(Object.keys(known).length ? { fingerprints: known } : {}) }
}

function sessionHeaders() {
    const stored = readStoredAuth() || {}
    const headers = {}
    SESSION_HEADERS.forEach(name => {
        const key = Object.keys(stored).find(key => key.toLowerCase() === name.toLowerCase())
        // Empty strings mask stale defaults; Axios 0.27 serializes null as the literal "null".
        headers[name] = key && stored[key] !== false && stored[key] != null ? stored[key] : ''
    })
    return headers
}

async function normalizeError(error) {
    if (axios.isCancel(error)) {
        return Object.assign(new Error('Запрос отменён'), { code: 0, domainCode: 'request_cancelled' })
    }
    const code = httpErrorStatus(error)
    let body = code ? error?.data || error : null
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
        try {
            body = body.size <= 65536 ? JSON.parse(await body.text()) : null
        } catch {
            body = null
        }
    }
    return Object.assign(
        new Error(body?.error?.message || body?.message || 'Не удалось связаться с сервером'),
        {
            code,
            domainCode: body?.error?.code || '',
            requestId: body?.meta?.request_id,
        }
    )
}

// The shared validator accepts only 200 and logs out on 401. Workspace endpoints may answer any 2xx,
// and the context probe runs on every page, so a backend without the workspace must not log users out.
function validateStatus(name) {
    return status => (status === 401 && name !== 'context' ? request.defaults.validateStatus(status) : true)
}

async function send(method, name, pathParams, options = {}) {
    const { mediaPreview = false, ...config } = options
    const url = endpoint(name, pathParams)
    try {
        const response = await request.request({
            timeout: 20000,
            ...config,
            method,
            url,
            headers: sessionHeaders(),
            paramsSerializer: serializeParams,
            validateStatus: validateStatus(name),
        })
        if (response.status < 200 || response.status >= 300) throw { code: response.status, data: response.data }
        return mediaPreview
            ? {
                blob: response.data,
                preview: response.headers['x-workspace-media-preview'] === '1',
                originalSize: Number(response.headers['x-workspace-media-original-size']) || null,
            }
            : response.data
    } catch (error) {
        throw await normalizeError(error)
    }
}

const chatParams = tab => ({ companyId: tab.company_id, chatId: tab.id })
const customerParams = tab => ({ companyId: tab.company_id, customerId: tab.customer_id })
const driverParams = (companyId, driver) => ({ companyId, parkId: driver.park_id, driverId: driver.id })
const fileParams = (tab, message) => ({ ...chatParams(tab), messageId: message.id })
const fileEndpoint = (message, name) => (message.archived ? 'archived' + name[0].toUpperCase() + name.slice(1) : name)

export default {
    context: options => send('get', 'context', {}, options),
    inbox: (params, options = {}) => send('get', 'inbox', {}, { ...options, params }),
    inboxWindow: (params, options = {}) => send('get', 'inboxWindow', {}, { ...options, params: windowParams(params) }),
    counters: (params, options = {}) => send('get', 'counters', {}, { ...options, params }),
    reference: (companyId, options) => send('get', 'reference', { companyId }, options),
    crmSession: (companyId, parkId, purpose = 'create_driver') =>
        send('post', 'crmSession', { companyId }, { data: { park_id: parkId || null, purpose } }),
    chat: (tab, options) => send('get', 'chat', chatParams(tab), options),
    chatStatuses: (chats, options = {}) => send('get', 'chatStatuses', {}, { ...options, params: { chats } }),
    messages: (tab, params, options = {}) => send('get', 'messages', chatParams(tab), { ...options, params }),
    action: (tab, action, data) => send('post', 'action', { ...chatParams(tab), action }, { data }),
    sendStatus: (tab, operationId) => send('get', 'sendStatus', { ...chatParams(tab), operationId }),
    resolveSend: (tab, operationId, data) => send('post', 'resolveSend', { ...chatParams(tab), operationId }, { data }),
    startChat: (companyId, data) => send('post', 'chats', { companyId }, { data }),
    archive: (companyId, params, options = {}) => send('get', 'archive', { companyId }, { ...options, params }),
    transcribe: (tab, message) =>
        send('post', fileEndpoint(message, 'transcribe'), fileParams(tab, message), { timeout: 75000 }),
    history: (tab, params, options = {}) => send('get', 'history', customerParams(tab), { ...options, params }),
    historyList: (tab, params, options = {}) => send('get', 'historyList', customerParams(tab), { ...options, params }),
    historyMessages: (tab, historyId, params, options = {}) =>
        send('get', 'historyMessages', { companyId: tab.company_id, historyId }, { ...options, params }),
    file: (tab, message, options) => send('get', fileEndpoint(message, 'file'), fileParams(tab, message), options),
    fileContent: (tab, message, options = {}) =>
        send('get', fileEndpoint(message, 'fileContent'), fileParams(tab, message), {
            ...options,
            responseType: 'blob',
        }),
    filePreview: (tab, message, options = {}) =>
        send('get', fileEndpoint(message, 'fileContent'), fileParams(tab, message), {
            ...options,
            params: { preview: 1 },
            responseType: 'blob',
            mediaPreview: true,
        }),
    cards: (tab, options = {}) =>
        send('get', 'cards', { companyId: tab.company_id }, { ...options, params: { phone: tab.phone } }),
    cardsPreview: (tab, options = {}) =>
        send('get', 'cards', { companyId: tab.company_id }, {
            ...options,
            params: { phone: tab.phone, with_profile: 1 },
        }),
    driver: (companyId, driver, options) => send('get', 'driver', driverParams(companyId, driver), options),
    driverTab: (companyId, driver, section, params, options = {}) =>
        send('get', 'driverTab', { ...driverParams(companyId, driver), section }, { ...options, params }),
    candidate: (companyId, candidateId, options) => send('get', 'candidate', { companyId, candidateId }, options),
    document: (companyId, driver, documentId, options) =>
        send('get', 'document', { ...driverParams(companyId, driver), documentId }, options),
    comment: (companyId, driver, comment) =>
        send('post', 'comment', driverParams(companyId, driver), { data: { comment } }),
    subscribe: data => send('put', 'subscription', {}, { data }),
    unsubscribe: () => send('delete', 'subscription', {}),
}
